import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  FaWhatsapp,
  FaPhoneAlt,
  FaArrowRight,
  FaArrowUp,
  FaUsers,
  FaPassport,
  FaChartLine,
  FaGlobe,
  FaBriefcase,
  FaPlaneDeparture,
  FaGraduationCap,
  FaBuilding,
  FaFileSignature,
  FaIdCard,
  FaCheckCircle,
  FaHandshake,
  FaShieldAlt,
  FaClock,
  FaFileAlt,
  FaStar,
  FaQuoteLeft,
} from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, EffectFade, Pagination } from "swiper/modules";
import CountUp from "react-countup";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/pagination";

/* ─── DATA ─────────────────────────────────── */

const HERO_SLIDES = [
  {
    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2070&auto=format&fit=crop",
    label: "IMMIGRATION & VISA CONSULTANCY",
    title: "Your Gateway to Global Opportunities",
    subtitle: "Expert immigration guidance for individuals, students and businesses. We simplify the visa journey from start to approval.",
  },
  {
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=2069&auto=format&fit=crop",
    label: "TRUSTED BY 800+ CLIENTS",
    title: "Professional Visa Support You Can Trust",
    subtitle: "With a 98% success rate and dedicated consultants for every client, we make international immigration straightforward.",
  },
  {
    image: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=2071&auto=format&fit=crop",
    label: "BUSINESSES & INVESTORS",
    title: "Expand Your Business Globally",
    subtitle: "From trade licenses and investor visas to corporate relocations — Lumis is your complete business immigration partner.",
  },
];

const SERVICES = [
  { slug: "work-visa", Icon: FaBriefcase, title: "Work Visa", desc: "Employment & skilled worker visas for global careers.", img: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&auto=format&fit=crop", color: "bg-blue-50 text-blue-600" },
  { slug: "travel-visa", Icon: FaPlaneDeparture, title: "Tourist Visa", desc: "Fast processing for short-stay and visit visas worldwide.", img: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?w=600&auto=format&fit=crop", color: "bg-sky-50 text-sky-600" },
  { slug: "student-visa", Icon: FaGraduationCap, title: "Student Visa", desc: "Study abroad support from university selection to approval.", img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=600&auto=format&fit=crop", color: "bg-green-50 text-green-600" },
  { slug: "business-visa", Icon: FaBuilding, title: "Business Visa", desc: "Investor & entrepreneur visas for global business expansion.", img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&auto=format&fit=crop", color: "bg-purple-50 text-purple-600" },
  { slug: "attestation", Icon: FaFileSignature, title: "Attestation", desc: "Document verification & legalisation for international use.", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&auto=format&fit=crop", color: "bg-orange-50 text-orange-500" },
  { slug: "trade-license", Icon: FaIdCard, title: "Trade License", desc: "UAE company formation & trade license processing.", img: "https://images.unsplash.com/photo-1556761175-4b46a572b786?w=600&auto=format&fit=crop", color: "bg-red-50 text-red-500" },
];

const COUNTRIES = [
  { code: "ca", name: "Canada", visas: "Work, PR, Student" },
  { code: "gb", name: "United Kingdom", visas: "Skilled Worker, Student" },
  { code: "au", name: "Australia", visas: "PR, Work, Student" },
  { code: "us", name: "United States", visas: "Work, Tourist, Student" },
  { code: "de", name: "Germany", visas: "Work, Student, Business" },
  { code: "ae", name: "UAE", visas: "Work, Investor, Tourist" },
  { code: "nz", name: "New Zealand", visas: "Work, PR, Student" },
  { code: "sg", name: "Singapore", visas: "Work, Business, Tourist" },
  { code: "fr", name: "France", visas: "Schengen, Student" },
  { code: "it", name: "Italy", visas: "Schengen, Work" },
];

const TESTIMONIALS = [
  { img: "men/32", name: "Ahmed R.", visa: "Canada Work Visa", flag: "🇨🇦", text: "Lumis made the entire process stress-free. My work visa was approved within the timeline they promised. Highly recommend!" },
  { img: "women/44", name: "Sarah M.", visa: "UK Skilled Worker Visa", flag: "🇬🇧", text: "Excellent consultants! From the initial meeting to visa approval, the team kept me informed every step of the way." },
  { img: "men/65", name: "David K.", visa: "Australia PR Visa", flag: "🇦🇺", text: "Fast, professional and incredibly detailed. I'm now a proud Australian permanent resident thanks to Lumis." },
  { img: "women/12", name: "Maria S.", visa: "Student Visa – Germany", flag: "🇩🇪", text: "They helped me with university selection right through to my student visa. My dream of studying in Germany came true!" },
  { img: "men/55", name: "Ravi Kumar", visa: "Singapore Employment", flag: "🇸🇬", text: "Professional team with deep immigration knowledge. My Singapore employment pass was approved without any issues." },
];

const WHY_CHOOSE = [
  { Icon: FaCheckCircle, title: "98% Success Rate", desc: "Industry-leading approval rates across all visa categories." },
  { Icon: FaHandshake, title: "Dedicated Consultant", desc: "A personal consultant guides your case from start to finish." },
  { Icon: FaShieldAlt, title: "Transparent Process", desc: "No hidden fees. Regular updates throughout your application." },
  { Icon: FaFileAlt, title: "Expert Documentation", desc: "Every document is verified and reviewed before submission." },
  { Icon: FaGlobe, title: "16+ Countries", desc: "Immigration expertise spanning countries on every continent." },
  { Icon: FaClock, title: "Fast Processing", desc: "We file applications accurately and on time, every time." },
];

function StarRating() {
  return (
    <div className="flex gap-0.5 text-yellow-400">
      {[...Array(5)].map((_, i) => <FaStar key={i} className="text-sm" />)}
    </div>
  );
}

/* ─── COMPONENT ────────────────────────────── */

export default function Home() {
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div>
      <Helmet>
        <title>Lumis Documents Clearing | Immigration & Visa Experts Dubai</title>
        <meta name="description" content="Expert immigration and visa consultancy in Dubai. Work, student, tourist, and business visas for Canada, UK, USA, Australia, and Europe with a 98% success rate." />
      </Helmet>

      {/* ════════════════════════════════════════
          HERO SLIDER
      ════════════════════════════════════════ */}
      <Swiper
        modules={[Autoplay, EffectFade, Pagination]}
        effect="fade"
        loop
        autoplay={{ delay: 4500, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        className="h-screen"
      >
        {HERO_SLIDES.map((slide, i) => (
          <SwiperSlide key={i}>
            <section className="h-screen flex items-center text-white relative overflow-hidden">
              <div
                className="absolute inset-0 bg-cover bg-center scale-105 transition-transform duration-[8000ms]"
                style={{ backgroundImage: `url('${slide.image}')` }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-transparent" />

              <div className="relative z-10 max-w-7xl mx-auto px-8 w-full">
                <div className="max-w-2xl">
                  <p className="tracking-widest text-blue-400 text-sm font-semibold mb-5 uppercase">
                    {slide.label}
                  </p>

                  <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                    {slide.title}
                  </h1>

                  <p className="text-gray-300 mb-10 text-lg leading-relaxed">
                    {slide.subtitle}
                  </p>

                  <div className="flex gap-4 flex-wrap">
                    <Link
                      to="/contact"
                      className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 rounded-full font-semibold shadow-xl flex items-center gap-3 transition"
                    >
                      Get Free Consultation <FaArrowRight />
                    </Link>
                    <Link
                      to="/services"
                      className="border border-white/70 backdrop-blur-sm px-8 py-3.5 rounded-full font-semibold flex items-center gap-3 hover:bg-white hover:text-black transition"
                    >
                      Our Services <FaArrowRight />
                    </Link>
                  </div>

                  {/* MINI TRUST INDICATORS */}
                  <div className="flex flex-wrap gap-6 mt-12 text-sm text-gray-300">
                    <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> 1,200+ Visas Processed</span>
                    <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> 98% Success Rate</span>
                    <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> Free Consultation</span>
                  </div>
                </div>
              </div>
            </section>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* ════════════════════════════════════════
          STATS BAR
      ════════════════════════════════════════ */}
      <section className="bg-blue-600 text-white py-10">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { Icon: FaPassport, end: 1200, suffix: "+", label: "Visas Processed" },
            { Icon: FaUsers, end: 800, suffix: "+", label: "Happy Clients" },
            { Icon: FaChartLine, end: 98, suffix: "%", label: "Success Rate" },
            { Icon: FaGlobe, end: 16, suffix: "+", label: "Countries Covered" },
          ].map(({ Icon, end, suffix, label }) => (
            <div key={label} className="flex flex-col items-center gap-1">
              <Icon className="text-blue-200 text-xl mb-1" />
              <h3 className="text-3xl font-bold">
                <CountUp end={end} duration={2.5} enableScrollSpy scrollSpyDelay={200} />{suffix}
              </h3>
              <p className="text-blue-100 text-sm">{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ════════════════════════════════════════
          SERVICES
      ════════════════════════════════════════ */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            What We Do
          </p>
          <h2 className="text-4xl font-bold text-center mb-3">
            Visa & Immigration Services
          </h2>
          <p className="text-center text-gray-500 mb-14 max-w-xl mx-auto">
            Professional assistance for every type of visa and immigration need — handled by certified consultants.
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
            {SERVICES.map(({ slug, Icon, title, desc, img, color }) => (
              <Link
                key={slug}
                to={`/services/${slug}`}
                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl hover:-translate-y-2 transition duration-300 overflow-hidden flex flex-col"
              >
                {/* IMAGE */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={img}
                    alt={title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className={`absolute top-3 right-3 w-10 h-10 ${color} bg-white rounded-xl flex items-center justify-center shadow`}>
                    <Icon className="text-lg" />
                  </div>
                </div>

                {/* CONTENT */}
                <div className="p-6 flex flex-col flex-1">
                  <h3 className="font-bold text-lg mb-2 text-gray-800 group-hover:text-blue-600 transition">
                    {title}
                  </h3>
                  <p className="text-gray-500 text-sm mb-4 flex-1">{desc}</p>
                  <span className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold group-hover:gap-3 transition">
                    Learn More <FaArrowRight className="text-xs" />
                  </span>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
            >
              View All Services <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          WHY CHOOSE LUMIS
      ════════════════════════════════════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* IMAGE */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=2070&auto=format&fit=crop"
                alt="Lumis team"
                className="rounded-2xl shadow-2xl w-full h-[480px] object-cover"
              />
              {/* FLOATING BADGE */}
              <div className="absolute -bottom-6 -right-6 bg-blue-600 text-white rounded-2xl p-6 shadow-xl hidden md:block">
                <p className="text-3xl font-bold">10+</p>
                <p className="text-blue-200 text-sm mt-1">Years Experience</p>
              </div>
              <div className="absolute top-6 -left-6 bg-white rounded-2xl shadow-xl p-5 hidden md:flex items-center gap-4">
                <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center">
                  <FaCheckCircle className="text-green-500 text-2xl" />
                </div>
                <div>
                  <p className="font-bold text-xl text-gray-800">98%</p>
                  <p className="text-xs text-gray-500">Visa Success Rate</p>
                </div>
              </div>
            </div>

            {/* CONTENT */}
            <div>
              <p className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
                Why Lumis
              </p>
              <h2 className="text-4xl font-bold mb-4 leading-tight">
                Why Hundreds Choose Lumis for Their Visa Journey
              </h2>
              <p className="text-gray-500 mb-10 leading-relaxed">
                At Lumis Documents Clearing, we combine expert knowledge with a
                deeply personalised approach. Our clients aren't case numbers —
                they're individuals with real goals, and we treat every
                application with that care.
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {WHY_CHOOSE.map(({ Icon, title, desc }) => (
                  <div key={title} className="group flex gap-4 items-start">
                    <div className="w-11 h-11 bg-blue-50 group-hover:bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 transition duration-300">
                      <Icon className="text-blue-600 group-hover:text-white text-lg transition duration-300" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-gray-800 mb-1 group-hover:text-blue-600 transition">{title}</h4>
                      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 mt-10">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-7 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
                >
                  About Us <FaArrowRight />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 border border-blue-600 text-blue-600 px-7 py-3 rounded-full font-semibold hover:bg-blue-50 transition"
                >
                  Get Consultation <FaArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          COUNTRIES SLIDER
      ════════════════════════════════════════ */}
      <section className="py-24 bg-gray-100 relative overflow-hidden">
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg"
          className="absolute opacity-5 w-full top-10 pointer-events-none select-none"
          alt=""
        />

        <div className="relative z-10">
          <p className="text-center text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Global Reach
          </p>
          <h2 className="text-4xl font-bold text-center mb-3">
            Countries We Serve
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Immigration and visa assistance for top global destinations
          </p>

          <div className="max-w-6xl mx-auto px-6">
            <Swiper
              modules={[Autoplay]}
              slidesPerView={4}
              spaceBetween={24}
              loop
              speed={1200}
              autoplay={{ delay: 2000, disableOnInteraction: false, pauseOnMouseEnter: true }}
              breakpoints={{
                320: { slidesPerView: 1 },
                480: { slidesPerView: 2 },
                768: { slidesPerView: 3 },
                1024: { slidesPerView: 4 },
              }}
            >
              {COUNTRIES.map(({ code, name, visas }) => (
                <SwiperSlide key={code}>
                  <div className="bg-white rounded-2xl p-7 text-center shadow-md hover:shadow-2xl hover:-translate-y-2 transition duration-300">
                    <img
                      src={`https://flagcdn.com/w80/${code}.png`}
                      className="h-14 w-14 mx-auto mb-4 rounded-full shadow-md object-cover"
                      alt={name}
                    />
                    <h3 className="font-bold text-lg text-gray-800 mb-1">{name}</h3>
                    <p className="text-xs text-gray-400">{visas}</p>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            <div className="text-center mt-12">
              <Link
                to="/countries"
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
              >
                Explore All Countries <FaArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          HOW IT WORKS
      ════════════════════════════════════════ */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            The Process
          </p>
          <h2 className="text-4xl font-bold text-center mb-3">How It Works</h2>
          <p className="text-center text-gray-500 mb-14">
            A simple, transparent process from your first call to visa approval
          </p>

          <div className="grid md:grid-cols-4 gap-8 text-center relative">
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-blue-100 z-0" />

            {[
              { step: "01", Icon: FaPhoneAlt, title: "Free Consultation", desc: "Book a free consultation. We assess your profile and best visa pathway." },
              { step: "02", Icon: FaFileAlt, title: "Document Collection", desc: "We give you a precise checklist and verify every document you submit." },
              { step: "03", Icon: FaHandshake, title: "Application Filing", desc: "We submit your application and manage all authority follow-ups." },
              { step: "04", Icon: FaPlaneDeparture, title: "Visa Approved!", desc: "Receive your visa and start your international journey with confidence." },
            ].map(({ step, Icon, title, desc }) => (
              <div key={step} className="relative z-10">
                <div className="w-16 h-16 bg-blue-600 text-white rounded-full flex items-center justify-center text-lg font-bold mx-auto mb-5 shadow-lg">
                  {step}
                </div>
                <div className="bg-white border border-gray-100 rounded-xl p-6 shadow-sm hover:shadow-lg transition">
                  <Icon className="text-blue-600 text-xl mx-auto mb-3" />
                  <h3 className="font-bold text-base mb-2">{title}</h3>
                  <p className="text-gray-500 text-sm">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          TESTIMONIALS
      ════════════════════════════════════════ */}
      <section className="py-24 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Client stories
          </p>
          <h2 className="text-4xl font-bold text-center mb-3">
            What Our Clients Say
          </h2>
          <p className="text-center text-gray-500 mb-14">
            Trusted by hundreds of clients across 16+ countries
          </p>

          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={3}
            spaceBetween={24}
            loop
            speed={1200}
            autoplay={{ delay: 2800, disableOnInteraction: false, pauseOnMouseEnter: true }}
            pagination={{ clickable: true }}
            className="pb-10"
            breakpoints={{
              320: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1024: { slidesPerView: 3 },
            }}
          >
            {TESTIMONIALS.map(({ img, name, visa, flag, text }) => (
              <SwiperSlide key={name}>
                <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition duration-300 flex flex-col h-full">
                  <FaQuoteLeft className="text-blue-100 text-4xl mb-4" />
                  <p className="text-gray-600 italic text-sm leading-relaxed mb-6 flex-1">
                    "{text}"
                  </p>
                  <div className="flex items-center gap-4 mt-auto">
                    <img
                      src={`https://randomuser.me/api/portraits/${img}.jpg`}
                      className="w-12 h-12 rounded-full object-cover shadow"
                      alt={name}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-gray-800 text-sm">{name}</h4>
                      <p className="text-xs text-gray-400 truncate">{flag} {visa}</p>
                    </div>
                    <StarRating />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className="text-center mt-10">
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-full font-semibold hover:bg-blue-700 transition shadow-md"
            >
              See All Testimonials <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          CTA BANNER
      ════════════════════════════════════════ */}
      <section
        className="relative py-28 text-white text-center bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1551836022-d5d88e9218df?q=80&w=2070&auto=format&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-blue-900/80" />
        <div className="relative z-10 max-w-3xl mx-auto px-6">
          <p className="text-blue-300 font-semibold text-sm uppercase tracking-wider mb-4">
            Start Today
          </p>
          <h2 className="text-4xl font-bold mb-5">
            Ready to Begin Your Immigration Journey?
          </h2>
          <p className="text-blue-100 mb-10 text-lg leading-relaxed">
            Speak with our expert consultants for a free assessment. We'll
            identify the best visa pathway for you and guide you every step of
            the way.
          </p>

          <div className="flex justify-center gap-4 flex-wrap">
            <Link
              to="/contact"
              className="bg-white text-blue-700 px-9 py-3.5 rounded-full font-bold hover:bg-blue-50 transition shadow-lg"
            >
              Get Free Consultation
            </Link>
            <a
              href="https://wa.me/971547662665"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-9 py-3.5 rounded-full font-bold transition shadow-lg"
            >
              <FaWhatsapp size={20} /> WhatsApp Us
            </a>
          </div>

          {/* TRUST ROW */}
          <div className="flex flex-wrap justify-center gap-8 mt-14 text-sm text-blue-200">
            <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> Free Initial Consultation</span>
            <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> No Hidden Fees</span>
            <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> 98% Success Rate</span>
            <span className="flex items-center gap-2"><FaCheckCircle className="text-green-400" /> Dedicated Consultant</span>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════
          FLOATING WIDGETS
      ════════════════════════════════════════ */}
      <div className="fixed bottom-6 right-6 flex flex-col items-end gap-4 z-50">
        <div className="bg-white shadow-xl rounded-lg p-4 max-w-xs animate-bounce">
          <p className="text-sm text-gray-700">💬 Need help with your visa?</p>
          <p className="text-sm font-semibold text-gray-900">Chat with us on WhatsApp</p>
        </div>

        <Link to="/contact" className="group flex items-center">
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Free Consultation
          </span>
          <div className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaPhoneAlt size={20} />
          </div>
        </Link>

        <a
          href="https://wa.me/971547662665"
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center"
        >
          <span className="mr-3 bg-gray-900 text-white text-sm px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition">
            Chat on WhatsApp
          </span>
          <div className="bg-green-500 text-white p-4 rounded-full shadow-lg hover:scale-110 transition">
            <FaWhatsapp size={22} />
          </div>
        </a>
      </div>

      {/* SCROLL TO TOP */}
      {showTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="fixed bottom-6 left-6 bg-blue-600 text-white p-3.5 rounded-full shadow-lg hover:scale-110 transition z-50"
          aria-label="Scroll to top"
        >
          <FaArrowUp size={16} />
        </button>
      )}
    </div>
  );
}
